import React from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Button } from '@freelancelabs/components';

import { ContactOnboardingStatus } from '../../../graphql/__generated__/schema';
import {
    AccessibleAccountsDocument,
    AssignablePartnerListsDocument,
    RolesDocument,
} from '../../../graphql/__generated__/types';
import { EMPTY } from '../display';
import ReviewAttachment from './ReviewAttachment';
import { contactFullName, contactStatusReading } from './contactLookup';
import { Draft } from './draft';
import { findRole, roleRefusal } from './grantableRoles';

interface Props {
    draft: Draft;
    onEditStep: (step: number) => void;
}

/**
 * Step 5: what is about to be created, before the invitation leaves.
 *
 * Each block carries the button back to its step. Unlike the mockup, going back is
 * not a one-way trip: the footer of every step offers the way back here, so
 * correcting the second block does not mean walking through the rest again.
 *
 * The perimeter is named with the same helper as the user sheet. A perimeter phrased
 * one way here and another way there reads as two different facts.
 */
const ReviewStep = ({ draft, onEditStep }: Props) => {
    const { data: accountsData } = useQuery(AccessibleAccountsDocument);
    const { data: rolesData } = useQuery(RolesDocument);

    const accountName = (organizationId: string) =>
        (accountsData?.accessibleAccounts ?? []).find((account) => account?.id === organizationId)?.name ??
        organizationId;
    const roles = (rolesData?.portal ?? []).filter(Boolean);
    const roleName = (roleId: string | null) => findRole(roles, roleId)?.name ?? EMPTY;

    /**
     * Why this line cannot be sent, or null. The footer already refuses on the same
     * fact; it is repeated here because the footer names the **role** and this list
     * names the **account**, and only together do they say which line to go and fix.
     */
    const refusalOf = (roleId: string | null) => {
        const role = findRole(roles, roleId);
        return role?.grantableByViewer === false ? roleRefusal(role.notGrantableReason).tag : null;
    };

    /**
     * Read from the cache step 4 already filled: the recap names the list it is about
     * to grant, and an id in a recap tells the operator nothing they can check. Falls
     * back to the id rather than to a blank: an unresolved name is worth showing as
     * the opaque string it is.
     */
    const { data: partnerListsData } = useQuery(AssignablePartnerListsDocument);
    const partnerListName =
        (partnerListsData?.assignablePartnerLists?.lists ?? []).find((list) => list?.id === draft.partnerListId)
            ?.name ?? draft.partnerListId;

    const dashboardCount = draft.dashboards.length;
    /**
     * The identity as the source writes it, read off the contact picked at step 1.
     *
     * Attention, never a fallback to « nom non renseigné » here: the identity is no longer
     * typed, so a missing name means no contact is attached to this draft, and the
     * footer refuses the send on that. Saying « non renseigné » would present it as a
     * blank the operator could shrug at, when it is the thing that blocks.
     */
    const contact = draft.contact;
    const fullName = contact ? (contactFullName(contact) ?? '') : '';
    const statusReading = contact ? contactStatusReading(contact.onboardingStatus) : null;
    // Resolved outside the template: ttag refuses a call expression inside a
    // localised string, and the extraction fails rather than the build.
    const contactReference = contact?.businessReference ?? EMPTY;

    return (
        <div className="flex flex-col gap-4">
            <section className="rounded-lg border border-border-norm p-4">
                <div className="flex flex-wrap items-start gap-3">
                    <div>
                        <h3 className="text-sm font-semibold text-text-900">{c('Onboarding recap')
                            .t`Identification`}</h3>
                        <p className="mt-1 font-mono text-sm">{draft.email}</p>
                        {contact ? (
                            <>
                                <p className="text-xs text-text-600">
                                    {fullName}
                                    {contact.jobFunction ? `, ${contact.jobFunction}` : ''}
                                </p>
                                {/* Where the identity comes from, named on the screen
                                    that validates it: the operator did not type it and
                                    cannot correct it here, so the recap has to say it
                                    was read off the source and which record it is. */}
                                <p className="mt-1 text-xs text-text-600">
                                    {c('Onboarding recap')
                                        .t`Identity read from the source — reference ${contactReference}.`}
                                </p>
                                {/* « Déjà onboardé » changes what the send does. It is
                                    not a refusal, so it must be said here rather than
                                    letting the operator read « invitation envoyée » for
                                    a person who already had an account. */}
                                {statusReading && !statusReading.selectable && (
                                    <p className="mt-1 text-sm text-danger-600" role="alert">
                                        {statusReading.meaning}
                                    </p>
                                )}
                                {statusReading?.selectable &&
                                    contact.onboardingStatus === ContactOnboardingStatus.Onboarded && (
                                        <p className="mt-1 text-xs text-text-600" role="status">
                                            {statusReading.meaning}
                                        </p>
                                    )}
                            </>
                        ) : (
                            <p className="text-sm text-danger-600" role="alert">
                                {c('Onboarding recap')
                                    .t`No contact is attached to this address any more — it changed after one was picked. Go back to step 1 and search it again: the invitation cannot be sent without an identity from the source.`}
                            </p>
                        )}
                    </div>
                    <Button className="ml-auto" size="small" category="weak" onClick={() => onEditStep(1)}>
                        {c('Onboarding action').t`Edit`}
                    </Button>
                </div>
            </section>

            <section className="rounded-lg border border-border-norm p-4">
                <div className="flex flex-wrap items-start gap-3">
                    <h3 className="text-sm font-semibold text-text-900">
                        {c('Onboarding recap').t`Accounts, roles and perimeters`}
                    </h3>
                    <Button className="ml-auto" size="small" category="weak" onClick={() => onEditStep(2)}>
                        {c('Onboarding action').t`Edit`}
                    </Button>
                </div>
                <ul className="mt-3 flex flex-col gap-2">
                    {draft.attachments.map((attachment) => (
                        <ReviewAttachment
                            key={attachment.organizationId}
                            attachment={attachment}
                            accountName={accountName(attachment.organizationId)}
                            roleName={roleName(attachment.roleId)}
                            refusal={refusalOf(attachment.roleId)}
                            email={draft.contact?.email ?? ''}
                        />
                    ))}
                </ul>
            </section>

            {/* The two optional attributions. They are recapped even when empty, and
                what is said is what the person will experience: « le module reste
                masqué » : rather than a blank or a dash. Granting nothing is the
                normal outcome while the catalogues have no writer, so the recap has to
                make it read as a decision and not as a step that failed. */}
            <section className="rounded-lg border border-border-norm p-4">
                <div className="flex flex-wrap items-start gap-3">
                    <h3 className="text-sm font-semibold text-text-900">{c('Onboarding recap').t`Dashboards`}</h3>
                    <Button className="ml-auto" size="small" category="weak" onClick={() => onEditStep(3)}>
                        {c('Onboarding action').t`Edit`}
                    </Button>
                </div>
                {draft.dashboards.length === 0 ? (
                    <p className="mt-2 text-xs text-text-600">
                        {c('Onboarding recap')
                            .t`No dashboard granted — the « Dashboard » module will stay hidden in this person's portal.`}
                    </p>
                ) : (
                    <p className="mt-2 text-xs text-text-600">
                        {ngettext(
                            msgid`${dashboardCount} dashboard granted.`,
                            `${dashboardCount} dashboards granted.`,
                            dashboardCount
                        )}
                    </p>
                )}
            </section>

            <section className="rounded-lg border border-border-norm p-4">
                <div className="flex flex-wrap items-start gap-3">
                    <h3 className="text-sm font-semibold text-text-900">{c('Onboarding recap').t`Partners`}</h3>
                    <Button className="ml-auto" size="small" category="weak" onClick={() => onEditStep(4)}>
                        {c('Onboarding action').t`Edit`}
                    </Button>
                </div>
                <p className="mt-2 text-xs text-text-600">
                    {draft.partnerListId
                        ? partnerListName
                        : c('Onboarding recap')
                              .t`No list associated — the « Partenaires » module will stay empty for this person.`}
                </p>
            </section>

            {/* The one step of the mockup still unbuilt, named rather than dropped: a
                recap silently missing a block reads as a block that was forgotten. */}
            <p className="text-xs text-text-600">
                {c('Onboarding').t`The referring account manager is not part of this journey: no query lists one yet.`}
            </p>
        </div>
    );
};

export default ReviewStep;
